import React, { useState, useContext } from 'react';
import { AuthContext } from '../Providers/AuthContext';
import { validateDOB, isAge16OrOlder } from '../../utils/ageUtils';
import { UserDataService } from '../../services/firebase/userDataService';
import styles from './DOBCollectionModal.module.css';

interface DOBCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function DOBCollectionModal({ isOpen, onClose }: DOBCollectionModalProps) {
  const { user, userProfile, updateProfile } = useContext(AuthContext);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate DOB
    const validation = validateDOB(dateOfBirth);
    if (!validation.valid) {
      setError(validation.error || 'Invalid date of birth');
      return;
    }

    setLoading(true);

    try {
      if (!user || !userProfile) {
        setError('Not logged in');
        setLoading(false);
        return;
      }

      // Determine chat eligibility based on age
      const chatEnabled = isAge16OrOlder(dateOfBirth);

      // Update profile with DOB
      const updatedProfile = {
        ...userProfile,
        dateOfBirth,
        chatEnabled
      };

      await UserDataService.updateUserProfile(user.uid, updatedProfile);
      await updateProfile(updatedProfile);

      onClose();
      window.location.reload();
    } catch (err: any) {
      setError('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>
          &times;
        </button>

        <h2 className={styles.title}>Add Date of Birth</h2>

        <p className={styles.description}>
          To enable chat features, please provide your date of birth.
          This helps us ensure age-appropriate content settings.
        </p>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Date of Birth</label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => {
                setDateOfBirth(e.target.value);
                setError('');
              }}
              className={styles.input}
              required
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <p className={styles.note}>
            Chat is available for users 16 and older.
          </p>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading || !dateOfBirth}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default DOBCollectionModal;
